import {
  Add as AddIcon,
  ConfirmationNumberOutlined as CaseIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useGetCustomersWorkItemsQuery } from "../../../../store/services/customerService";
import { useGetCustomerWorksQuery } from "../../../../store/services/timelineApiRTK";
import {
  Work,
  WorkItemList,
  WorkItemStateEnum,
} from "../../../../store/types/work.types";
import CompleteActionDialog from "../../../activity-complete-dialog/CompleteActionDialog";
import CreateCaseDialog from "../../../create-case/CreateCaseDialog";
import VisualActionDialog from "../../../next-action/VisualActionDialog";
import CaseLane from "./CaseLane";
import ContactAttemptGroup from "./ContactAttemptGroup";
import FreeActivityItem from "./FreeActivityItem";
import TimelineFilters, { TimelineFilter } from "./TimelineFilters";
import {
  DATE_BUCKET_LABEL,
  DATE_BUCKET_ORDER,
  DateBucket,
  INK,
  INK_3,
  LINE,
  ORANGE,
  getDateBucket,
  isContactAttempt,
  isFutureItem,
} from "./types";

interface MixedTimelineProps {
  customerNo: number;
}

const CLOSED_PREVIEW = 5;

const isRowUpcoming = (row: Row): boolean =>
  row.kind === "case"
    ? row.work.State === 1
    : row.item.State === WorkItemStateEnum.Active;

const isRowClosed = (row: Row): boolean =>
  row.kind === "case"
    ? row.work.State === 2
    : row.item.State === WorkItemStateEnum.Closed;

const ts = (s: string | null | undefined): number =>
  s ? new Date(s).getTime() : 0;

type Row =
  | { kind: "case"; work: Work; sortAt: number }
  | { kind: "free"; item: WorkItemList; sortAt: number };

const MixedTimeline = ({ customerNo }: MixedTimelineProps) => {
  const { data: works = [], isLoading: worksLoading } =
    useGetCustomerWorksQuery(customerNo);
  const { data: workItems = [], isLoading: itemsLoading } =
    useGetCustomersWorkItemsQuery(customerNo);

  const [filter, setFilter] = useState<TimelineFilter>("all");
  const [openLanes, setOpenLanes] = useState<Record<number, boolean>>({});
  const [touched, setTouched] = useState<Record<number, boolean>>({});
  const [showAllClosed, setShowAllClosed] = useState(false);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    workId?: number;
  }>({ open: false });
  const [completeTarget, setCompleteTarget] = useState<WorkItemList | null>(
    null
  );
  const [createCaseOpen, setCreateCaseOpen] = useState(false);

  const workIds = useMemo(() => new Set(works.map((w) => w.Id)), [works]);

  const childrenByWork = useMemo(() => {
    const map = new Map<number, WorkItemList[]>();
    for (const wi of workItems) {
      if (wi.WorkId != null && workIds.has(wi.WorkId)) {
        const list = map.get(wi.WorkId) ?? [];
        list.push(wi);
        map.set(wi.WorkId, list);
      }
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const aFuture = isFutureItem(a) ? 0 : 1;
        const bFuture = isFutureItem(b) ? 0 : 1;
        if (aFuture !== bFuture) return aFuture - bFuture;
        return ts(b.ScheduledTo) - ts(a.ScheduledTo);
      });
    }
    return map;
  }, [workItems, workIds]);

  const freeActivities = useMemo(
    () =>
      workItems.filter((wi) => wi.WorkId == null || !workIds.has(wi.WorkId)),
    [workItems, workIds]
  );

  /** Each item gets a "senaste händelse"-timestamp.
   *  - case: max of work.Modified and any child Modified/ScheduledTo
   *  - free activity: max of Modified/ScheduledTo/Created
   */
  const rows = useMemo<Row[]>(() => {
    const caseRows: Row[] = works.map((w) => {
      const kids = childrenByWork.get(w.Id) ?? [];
      const childMax = kids.reduce(
        (acc, k) => Math.max(acc, ts(k.Modified), ts(k.ScheduledTo)),
        0
      );
      const sortAt = Math.max(ts(w.Modified), ts(w.Created), childMax);
      return { kind: "case", work: w, sortAt };
    });
    const freeRows: Row[] = freeActivities.map((i) => ({
      kind: "free",
      item: i,
      sortAt: Math.max(ts(i.Modified), ts(i.ScheduledTo), ts(i.Created)),
    }));
    return [...caseRows, ...freeRows].sort((a, b) => b.sortAt - a.sortAt);
  }, [works, freeActivities, childrenByWork]);

  const filteredRows = useMemo<Row[]>(() => {
    switch (filter) {
      case "open":
        return rows.filter((r) => r.kind === "case" && r.work.State === 1);
      case "cases":
        return rows.filter((r) => r.kind === "case");
      case "activities":
        return rows.filter((r) => r.kind === "free");
      case "future":
        return rows.filter((r) => {
          if (r.kind === "free") return isFutureItem(r.item);
          const kids = childrenByWork.get(r.work.Id) ?? [];
          return kids.some(isFutureItem);
        });
      default:
        return rows;
    }
  }, [rows, filter, childrenByWork]);

  const upcomingRows = useMemo(
    () => filteredRows.filter(isRowUpcoming),
    [filteredRows]
  );

  /** "Next happening" timestamp for bucketing upcoming rows.
   *  - case: earliest ScheduledTo among future active children (else null)
   *  - free: own ScheduledTo
   */
  const nextAtForRow = (row: Row): string | null => {
    if (row.kind === "free") return row.item.ScheduledTo ?? null;
    const kids = childrenByWork.get(row.work.Id) ?? [];
    const futureScheduled = kids
      .filter((k) => k.State === WorkItemStateEnum.Active && k.ScheduledTo)
      .map((k) => k.ScheduledTo as string)
      .sort();
    return futureScheduled[0] ?? null;
  };

  const upcomingByBucket = useMemo(() => {
    const map = new Map<DateBucket, Row[]>();
    for (const row of upcomingRows) {
      const bucket = getDateBucket(nextAtForRow(row));
      const list = map.get(bucket) ?? [];
      list.push(row);
      map.set(bucket, list);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingRows, childrenByWork]);

  const closedRows = useMemo(
    () => filteredRows.filter(isRowClosed),
    [filteredRows]
  );
  const visibleClosed = showAllClosed
    ? closedRows
    : closedRows.slice(0, CLOSED_PREVIEW);
  const hiddenClosed = closedRows.length - visibleClosed.length;

  const isOpen = (workId: number) => {
    if (touched[workId]) return openLanes[workId];
    const work = works.find((w) => w.Id === workId);
    if (!work) return false;
    if (work.State === 1) return true;
    const kids = childrenByWork.get(workId) ?? [];
    return kids.some(isFutureItem);
  };

  const toggleLane = (workId: number) => {
    setTouched((t) => ({ ...t, [workId]: true }));
    setOpenLanes((s) => ({ ...s, [workId]: !isOpen(workId) }));
  };

  const counts = useMemo(() => {
    return {
      all: works.length + freeActivities.length,
      open: works.filter((w) => w.State === 1).length,
      cases: works.length,
      activities: freeActivities.length,
      future: rows.filter((r) =>
        r.kind === "free"
          ? isFutureItem(r.item)
          : (childrenByWork.get(r.work.Id) ?? []).some(isFutureItem)
      ).length,
    };
  }, [works, freeActivities, rows, childrenByWork]);

  const isEmpty =
    !worksLoading &&
    !itemsLoading &&
    works.length === 0 &&
    workItems.length === 0;
  const filterFutureOnly = filter === "future";

  const handleCompleteActivity = (item: WorkItemList) => setCompleteTarget(item);

  const renderRow = (row: Row) => {
    if (row.kind === "case") {
      const allKids = childrenByWork.get(row.work.Id) ?? [];
      const visibleKids = filterFutureOnly
        ? allKids.filter(isFutureItem)
        : allKids;
      return (
        <CaseLane
          key={`c-${row.work.Id}`}
          work={row.work}
          allChildren={allKids}
          visibleChildren={visibleKids}
          customerNo={customerNo}
          isOpen={isOpen(row.work.Id)}
          onToggle={() => toggleLane(row.work.Id)}
          onAddActivity={() =>
            setDialogState({ open: true, workId: row.work.Id })
          }
          onCompleteActivity={handleCompleteActivity}
          futureFilterActive={filterFutureOnly}
        />
      );
    }
    return (
      <FreeActivityItem
        key={`f-${row.item.Id}`}
        item={row.item}
        customerNo={customerNo}
        onComplete={handleCompleteActivity}
      />
    );
  };

  // Renderar en rad-lista men slår ihop konsekutiva lösa kontaktförsök till
  // en hopstaplad grupp så de inte tar upp onödig plats.
  const renderRows = (list: Row[]) => {
    const out: React.ReactNode[] = [];
    let attemptRun: WorkItemList[] = [];
    const flush = () => {
      if (attemptRun.length === 0) return;
      const run = attemptRun;
      attemptRun = [];
      out.push(
        <ContactAttemptGroup
          key={`caf-${run[0].Id}`}
          items={run}
          customerNo={customerNo}
          variant="free"
        />
      );
    };
    for (const row of list) {
      if (row.kind === "free" && isContactAttempt(row.item.Type)) {
        attemptRun.push(row.item);
      } else {
        flush();
        out.push(renderRow(row));
      }
    }
    flush();
    return out;
  };

  const BucketLabel = ({
    bucket,
    count,
  }: {
    bucket: DateBucket;
    count: number;
  }) => {
    const overdue = bucket === "overdue";
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={0.85}
        sx={{
          pt: 0.25,
          pb: 0.25,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: ".06em",
          fontWeight: 600,
          color: overdue ? "#B2433A" : INK_3,
        }}
      >
        <Box>{DATE_BUCKET_LABEL[bucket]}</Box>
        <Box
          component="span"
          sx={{
            fontSize: 11,
            px: 0.75,
            borderRadius: 999,
            bgcolor: overdue ? "#FBE9E7" : "rgba(0,0,0,.05)",
            color: overdue ? "#B2433A" : INK_3,
            fontWeight: 600,
            letterSpacing: 0,
          }}
        >
          {count}
        </Box>
      </Stack>
    );
  };

  const SectionLabel = ({
    children,
    count,
  }: {
    children: React.ReactNode;
    count: number;
  }) => (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        pt: 0.5,
        pb: 0.5,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        fontWeight: 700,
        color: INK,
      }}
    >
      <Box>{children}</Box>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          px: 0.85,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,.05)",
          color: "text.secondary",
          fontWeight: 600,
          letterSpacing: 0,
        }}
      >
        {count}
      </Box>
    </Stack>
  );

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: `1px solid ${LINE}`,
        bgcolor: "background.paper",
        boxShadow:
          "0 1px 2px rgba(24,22,20,.04), 0 1px 1px rgba(24,22,20,.03)",
      }}
    >
      <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1 }}
          flexWrap="wrap"
        >
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "6px",
                bgcolor: alpha(ORANGE, 0.1),
                color: ORANGE,
                display: "grid",
                placeItems: "center",
                "& .MuiSvgIcon-root": { fontSize: 14 },
              }}
            >
              <HistoryIcon />
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".08em",
                color: INK,
              }}
            >
              Tidslinje · ärenden + aktiviteter
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CaseIcon />}
              onClick={() => setCreateCaseOpen(true)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                color: ORANGE,
                borderColor: alpha(ORANGE, 0.4),
                "&:hover": {
                  borderColor: ORANGE,
                  bgcolor: alpha(ORANGE, 0.06),
                },
              }}
            >
              Skapa ärende
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogState({ open: true })}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                bgcolor: ORANGE,
                color: "#fff",
                boxShadow: `0 1px 0 rgba(0,0,0,.04), 0 6px 16px -8px ${alpha(ORANGE, 0.6)}`,
                "&:hover": { bgcolor: "#E55100" },
              }}
            >
              Ny aktivitet
            </Button>
          </Stack>
        </Stack>

        <TimelineFilters
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setShowAllClosed(false);
          }}
          counts={counts}
        />

        {worksLoading || itemsLoading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={22} />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body2">Inga ärenden eller aktiviteter än</Typography>
          </Box>
        ) : (
          <Stack gap={1.5}>
            {upcomingRows.length > 0 && (
              <>
                <SectionLabel count={upcomingRows.length}>Kommande</SectionLabel>
                {DATE_BUCKET_ORDER.map((bucket) => {
                  const bucketRows = upcomingByBucket.get(bucket);
                  if (!bucketRows || bucketRows.length === 0) return null;
                  return (
                    <Stack key={bucket} gap={1} sx={{ pl: 0.25 }}>
                      <BucketLabel bucket={bucket} count={bucketRows.length} />
                      <Stack gap={1.75}>{bucketRows.map(renderRow)}</Stack>
                    </Stack>
                  );
                })}
              </>
            )}

            {closedRows.length > 0 && (
              <>
                <SectionLabel count={closedRows.length}>Avslutade</SectionLabel>
                <Stack gap={1.75}>{renderRows(visibleClosed)}</Stack>
                {hiddenClosed > 0 && (
                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      size="small"
                      onClick={() => setShowAllClosed(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: ORANGE,
                      }}
                    >
                      Visa alla avslutade ({hiddenClosed} till)
                    </Button>
                  </Box>
                )}
                {showAllClosed && closedRows.length > CLOSED_PREVIEW && (
                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      size="small"
                      onClick={() => setShowAllClosed(false)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      Visa färre
                    </Button>
                  </Box>
                )}
              </>
            )}

            {upcomingRows.length === 0 && closedRows.length === 0 && (
              <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body2">
                  Inga rader matchar filtret
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>

      <VisualActionDialog
        open={dialogState.open}
        customerNo={customerNo}
        workId={dialogState.workId}
        onClose={() => setDialogState({ open: false })}
      />

      <CreateCaseDialog
        open={createCaseOpen}
        customerNo={customerNo}
        onClose={() => setCreateCaseOpen(false)}
      />

      {completeTarget && (
        <CompleteActionDialog
          open
          customerNo={completeTarget.Customer?.Id ?? customerNo}
          workItemNo={completeTarget.Id}
          onCancel={() => setCompleteTarget(null)}
          onClose={() => setCompleteTarget(null)}
          onComplete={() => setCompleteTarget(null)}
          onCompleteWithNextAction={() => {
            const workId = completeTarget.WorkId ?? undefined;
            setCompleteTarget(null);
            setDialogState({ open: true, workId });
          }}
        />
      )}
    </Card>
  );
};

export default MixedTimeline;
